(() => {
  if (window.ProductUpsellsBlockV5Loaded) return;
  window.ProductUpsellsBlockV5Loaded = true;

  const selectors = {
    root: '[data-product-upsells]',
    card: '[data-upsell-card]',
    checkbox: '[data-upsell-checkbox]',
    toggle: '[data-upsell-toggle]',
    propertyWrap: '[data-upsell-property-wrap]',
    propertyInput: '[data-upsell-property-input]',
    paymentButton: ':is(.product-purchase-bar__buttons, .product-sticky-bar__buttons) .shopify-payment-button'
  };

  const paymentButtonObservers = new WeakMap();

  const getAddUrl = () => `${Shopify.routes.root}cart/add.js`;
  const getCartUrl = () => `${Shopify.routes.root}cart.js`;

  function formatWithDelimiters(cents, precision = 2, thousands = ',', decimal = '.') {
    const amount = (Number(cents) / 100).toFixed(precision);
    const [whole, fraction] = amount.split('.');
    const groupedWhole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return fraction ? `${groupedWhole}${decimal}${fraction}` : groupedWhole;
  }

  function formatMoney(cents) {
    const settings = window.themeVariables?.settings || {};
    const format = settings.currencyCodeEnabled ? settings.moneyWithCurrencyFormat : settings.moneyFormat;
    const moneyFormat = format || '${{amount}}';
    const placeholder = moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);

    if (!placeholder) return formatWithDelimiters(cents);

    const formats = {
      amount: [2, ',', '.'],
      amount_no_decimals: [0, ',', '.'],
      amount_with_comma_separator: [2, '.', ','],
      amount_no_decimals_with_comma_separator: [0, '.', ','],
      amount_with_apostrophe_separator: [2, "'", '.'],
      amount_no_decimals_with_space_separator: [0, ' ', ','],
      amount_with_space_separator: [2, ' ', ',']
    };
    const formatOptions = formats[placeholder[1]] || formats.amount;
    const formattedAmount = formatWithDelimiters(cents, ...formatOptions);
    return moneyFormat.replace(placeholder[0], formattedAmount);
  }

  function replaceMoneyText(priceElement, amount) {
    if (!priceElement) return;

    Array.from(priceElement.childNodes).forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) node.remove();
    });
    priceElement.append(document.createTextNode(formatMoney(amount)));
  }

  function getSelectedUpsellPrices(root) {
    return Array.from(root.querySelectorAll(`${selectors.card}.is-selected`)).reduce((totals, card) => {
      const salePrice = card.querySelector('sale-price[data-price-amount]');
      if (!salePrice) return totals;

      const displayedPrice = Number(salePrice.dataset.priceAmount) || 0;
      const referencePrice = Number(salePrice.dataset.referencePriceAmount) || displayedPrice;
      totals.displayed += displayedPrice;
      totals.reference += referencePrice;
      return totals;
    }, { displayed: 0, reference: 0 });
  }

  function updatePurchaseBarPrices(root) {
    if (!root) return;

    const section = root.closest('.shopify-section--main-product') || document;
    const upsellPrices = getSelectedUpsellPrices(root);

    section.querySelectorAll('.product-purchase-bar__price, .product-sticky-bar__price').forEach((priceContainer) => {
      const salePrice = priceContainer.querySelector('sale-price[data-price-amount]');
      const compareAtPrice = priceContainer.querySelector('compare-at-price[data-price-amount]');
      if (!salePrice) return;

      const baseDisplayedPrice = Number(salePrice.dataset.priceAmount) || 0;
      replaceMoneyText(salePrice, baseDisplayedPrice + upsellPrices.displayed);

      if (compareAtPrice) {
        const baseReferencePrice = Number(compareAtPrice.dataset.priceAmount) || baseDisplayedPrice;
        replaceMoneyText(compareAtPrice, baseReferencePrice + upsellPrices.reference);
      }
    });
  }

  function updateDynamicCheckoutState(root) {
    if (!root) return;

    const section = root.closest('.shopify-section--main-product') || document;
    const isDisabled = getSelectedCount(root) > 0;

    section.querySelectorAll(selectors.paymentButton).forEach((paymentButton) => {
      paymentButton.toggleAttribute('data-upsell-disabled', isDisabled);
      paymentButton.toggleAttribute('inert', isDisabled);

      if (isDisabled) {
        paymentButton.setAttribute('aria-disabled', 'true');
      } else {
        paymentButton.removeAttribute('aria-disabled');
      }
    });
  }

  function observeDynamicCheckout(root) {
    if (paymentButtonObservers.has(root)) return;

    const section = root.closest('.shopify-section--main-product');
    if (!section) return;

    let updateQueued = false;
    const observer = new MutationObserver(() => {
      if (!root.isConnected) {
        observer.disconnect();
        paymentButtonObservers.delete(root);
        return;
      }

      if (updateQueued) return;
      updateQueued = true;

      requestAnimationFrame(() => {
        updateQueued = false;
        updateDynamicCheckoutState(root);
      });
    });

    observer.observe(section, { childList: true, subtree: true });
    paymentButtonObservers.set(root, observer);
  }

  function getRootForForm(form) {
    const matchingRoots = Array.from(document.querySelectorAll(selectors.root)).filter((root) => {
      const formId = root.dataset.productFormId;
      if (formId && (form.id === formId || form.name === formId)) return true;

      const section = root.closest('.shopify-section');
      return section ? section.contains(form) : false;
    });

    return matchingRoots.find((root) => root.querySelector(`${selectors.checkbox}:checked`)) || matchingRoots[0] || null;
  }

  function syncCard(card) {
    const checkbox = card?.querySelector(selectors.checkbox);
    const toggle = card?.querySelector(selectors.toggle);
    const propertyWrap = card?.querySelector(selectors.propertyWrap);
    const propertyInput = card?.querySelector(selectors.propertyInput);
    const isSelected = Boolean(checkbox?.checked);

    card?.classList.toggle('is-selected', isSelected);

    if (toggle) {
      toggle.textContent = isSelected ? toggle.dataset.removeLabel : toggle.dataset.addLabel;
      toggle.setAttribute('aria-pressed', String(isSelected));
    }

    if (propertyWrap) propertyWrap.hidden = !isSelected;
    if (propertyInput) propertyInput.disabled = !isSelected;
  }

  function resizeTextarea(textarea) {
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }

  function setInitialAccordionState(root) {
    if (root.dataset.initialOpenApplied === 'true') return;

    const details = root.querySelector(':scope > accordion-disclosure > details');
    if (!details) return;

    const mode = root.dataset.defaultOpen || 'none';
    const isDesktop = window.matchMedia('(min-width: 750px)').matches;
    details.open = mode === 'all' || (mode === 'desktop' && isDesktop) || (mode === 'mobile' && !isDesktop);
    details.setAttribute('aria-expanded', String(details.open));
    root.dataset.initialOpenApplied = 'true';
  }

  async function initAsyncRelated(root) {
    if (!root.dataset.relatedRecommendationsUrl || root.dataset.relatedRecommendationsBound === 'true') return;
    root.dataset.relatedRecommendationsBound = 'true';

    try {
      const response = await fetch(root.dataset.relatedRecommendationsUrl, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Related recommendations request failed');

      const holder = document.createElement('div');
      holder.innerHTML = await response.text();
      const freshRoot = holder.querySelector(`#${CSS.escape(root.id)}[data-product-upsells]`);

      if (!freshRoot?.querySelector(selectors.card)) {
        root.remove();
        return;
      }

      freshRoot.removeAttribute('hidden');
      root.replaceWith(freshRoot);
      bindRoot(freshRoot);
    } catch (error) {
      console.error(error);
      root.remove();
    }
  }

  function bindRoot(root) {
    if (root.dataset.bound === 'true') return;
    root.dataset.bound = 'true';

    root.querySelectorAll(selectors.card).forEach(syncCard);
    setInitialAccordionState(root);

    root.addEventListener('change', (event) => {
      const checkbox = event.target.closest(selectors.checkbox);
      if (checkbox) {
        syncCard(checkbox.closest(selectors.card));
        updatePurchaseBarPrices(root);
        updateDynamicCheckoutState(root);
      }
    });

    root.addEventListener('click', (event) => {
      const toggle = event.target.closest(selectors.toggle);
      if (!toggle) return;

      const card = toggle.closest(selectors.card);
      const checkbox = card?.querySelector(selectors.checkbox);
      if (!checkbox || checkbox.disabled) return;

      checkbox.checked = !checkbox.checked;
      checkbox.dispatchEvent(new Event('change', { bubbles: true }));
    });

    root.addEventListener('input', (event) => {
      const textarea = event.target.closest(selectors.propertyInput);
      if (textarea) resizeTextarea(textarea);
    });

    initAsyncRelated(root);
    updatePurchaseBarPrices(root);
    updateDynamicCheckoutState(root);
    observeDynamicCheckout(root);
  }

  function getSelectedCount(root) {
    return root ? root.querySelectorAll(`${selectors.checkbox}:checked`).length : 0;
  }

  function getThemeSectionsToBundle() {
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections }
    }));
    return sections;
  }

  function formToMainItem(form) {
    const formData = new FormData(form);
    const id = formData.get('id');
    if (!id) return null;

    const item = { id: Number(id), quantity: Number(formData.get('quantity') || 1) };
    const sellingPlan = formData.get('selling_plan');
    if (sellingPlan) item.selling_plan = Number(sellingPlan);

    const properties = {};
    formData.forEach((value, key) => {
      const match = key.match(/^properties\[(.+)]$/);
      if (match && value !== '') properties[match[1]] = value;
    });
    if (Object.keys(properties).length > 0) item.properties = properties;

    return item;
  }

  function getSelectedUpsellItems(root, mainQuantity) {
    const quantity = root.dataset.quantityMode === 'match_main' ? mainQuantity : 1;
    const items = [];

    root.querySelectorAll(selectors.card).forEach((card) => {
      const checkbox = card.querySelector(selectors.checkbox);
      if (!checkbox?.checked || checkbox.disabled) return;

      const item = { id: Number(card.dataset.variantId), quantity };
      const propertyInput = card.querySelector(selectors.propertyInput);

      if (propertyInput?.value.trim()) {
        item.properties = {
          [propertyInput.dataset.propertyName || 'Custom text']: propertyInput.value.trim()
        };
      }

      items.push(item);
    });

    return items;
  }

  function validateRoot(root) {
    if (root.dataset.engravingRequired !== 'true') return true;

    const selectedEngravingCards = root.querySelectorAll(`${selectors.card}[data-is-engraving="true"].is-selected`);
    for (const card of selectedEngravingCards) {
      const input = card.querySelector(selectors.propertyInput);
      if (input && input.value.trim() === '') {
        input.focus();
        input.reportValidity?.();
        return false;
      }
    }

    return true;
  }

  function dispatchCartError(form, errorMessage) {
    form.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: errorMessage } }));
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }

  async function addItemsWithThemeSync(items, form) {
    const sections = getThemeSectionsToBundle();
    const onSuccessDo = new FormData(form).get('on_success');
    const response = await fetch(getAddUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        items,
        sections: sections.join(','),
        sections_url: `${Shopify.routes.root}variants/${items[0].id}`
      })
    });
    const addResponse = await response.json().catch(() => ({}));

    if (!response.ok) throw new Error(addResponse?.description || addResponse?.message || 'Cart add error');

    if (window.themeVariables?.settings?.cartType === 'page' || window.themeVariables?.settings?.pageType === 'cart') {
      window.location.href = `${Shopify.routes.root}cart`;
      return;
    }

    const cart = await fetch(getCartUrl(), { headers: { Accept: 'application/json' } }).then((cartResponse) => cartResponse.json());
    cart.sections = addResponse.sections || {};
    const addedItems = Object.prototype.hasOwnProperty.call(addResponse, 'items') ? addResponse.items : [addResponse];

    form.dispatchEvent(new CustomEvent('variant:add', {
      bubbles: true,
      detail: { items: addedItems, cart, onSuccessDo }
    }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: { baseEvent: 'variant:add', onSuccessDo, cart }
    }));
    document.dispatchEvent(new CustomEvent('product-upsells:cart-updated', { bubbles: true, detail: { cart } }));
  }

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[action*="/cart/add"]');
    if (!form) return;

    const root = getRootForForm(form);
    if (!root || getSelectedCount(root) === 0) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }
    if (!validateRoot(root)) return;

    const mainItem = formToMainItem(form);
    if (!mainItem) return;

    const upsellItems = getSelectedUpsellItems(root, mainItem.quantity);
    if (upsellItems.length === 0) return;

    const submitButtons = form.querySelectorAll('[type="submit"]');
    submitButtons.forEach((button) => button.setAttribute('aria-busy', 'true'));
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    try {
      await addItemsWithThemeSync([mainItem, ...upsellItems], form);
    } catch (error) {
      const errorMessage = root.dataset.addErrorMessage || error.message;
      dispatchCartError(form, errorMessage);
      alert(errorMessage);
    } finally {
      submitButtons.forEach((button) => button.removeAttribute('aria-busy'));
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  }, true);

  document.addEventListener('click', (event) => {
    if (!event.target.closest(`${selectors.paymentButton}[data-upsell-disabled]`)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);

  function init(container = document) {
    container.querySelectorAll(selectors.root).forEach(bindRoot);
  }

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('product:rerender', (event) => {
    const root = getRootForForm(event.target);
    if (root) {
      requestAnimationFrame(() => {
        updatePurchaseBarPrices(root);
        updateDynamicCheckoutState(root);
      });
    }
  }, true);
  init();
})();
